<!--
ChannelTemplateModal — a camada Modal do par gatilho x canal x idioma.
Registrada em ModalManager e aberta por eventBus
('modal.channelTemplate.open'), nunca por ref (CLAUDE.md regra 8).

Abre sempre num PAR (gatilho x tipo de canal), a partir da variante padrão da
linha clicada; trocar de idioma e criar a variante que falta são ações de
dentro do formulário.
-->
<template>
  <template v-if="isVisible && row">
    <Modal
      :show="true"
      :title="modalTitle"
      size="large"
      :close-label="t('Fechar')"
      :save-label="t('Salvar')"
      :save-disabled-fn="isSaveDisabled"
      :save-fn="handleSubmit"
      save-test-id="channel-template-submit"
      @close="handleClose"
      @dismiss="handleClose"
    >
      <channel-template-form
        ref="formRef"
        :row="row"
        :triggers="triggers"
        @saved="handleSaved"
        @validation-failed="handleValidationFailed"
      />
    </Modal>
  </template>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import Modal from '@/ui/Modal.vue'
import ChannelTemplateForm from './ChannelTemplateForm.vue'
import { eventBus } from '@/eventBus'
import { log } from '@/utils/DebugLogger'
import { FRANCHISOR_TRIGGER_LABELS } from './franchisorTriggerLabels'
import { useFranchisorChannels } from '@/composables/useFranchisorChannels'
import type { FranchisorChannels_TemplateRow } from '@/contracts/FranchisorChannels_TemplateRow'

interface FormInstance {
  canSave: boolean
  save: () => Promise<void>
}

const { t } = useI18n()
const { triggers } = useFranchisorChannels()

const isVisible = ref(false)
const row = ref<FranchisorChannels_TemplateRow | null>(null)
const formRef = ref<FormInstance | null>(null)

// O título é o PAR; o idioma não entra nele porque muda dentro do modal, e
// um título que se contradiz com o seletor logo abaixo é pior que um genérico.
const modalTitle = computed(() => {
  if (row.value === null) return t('Template')
  const label = t(FRANCHISOR_TRIGGER_LABELS[row.value.trigger_key ?? ''] ?? (row.value.trigger_key ?? ''))
  const channel = row.value.channel_type === 'email' ? t('E-mail') : t('SMS')
  return `${label} — ${channel}`
})

const isSaveDisabled = (): boolean => !formRef.value?.canSave

function handleClose(): void {
  isVisible.value = false
  row.value = null
}

async function handleSubmit(): Promise<void> {
  if (!formRef.value) return
  try {
    await formRef.value.save()
  } catch (err) {
    // A recusa nomeada do backend ('unknown_template_var:<x>',
    // 'template_variant_exists', …) é o que o staff precisa ler — passa
    // verbatim, sem virar "erro ao salvar".
    const detail = err instanceof Error ? err.message : String(err)
    log('ChannelTemplateModal', 'handleSubmit', 'Erro ao salvar template', detail)
    eventBus.emit('globalMessage.error', t('Erro ao salvar template') + ': ' + detail)
  }
}

function handleSaved(): void {
  eventBus.emit('channelTemplate.saved')
  handleClose()
}

function handleValidationFailed(): void {
  eventBus.emit('globalMessage.error', t('Preencha os campos obrigatórios'))
}

function handleOpen(payload?: { row?: FranchisorChannels_TemplateRow | null }): void {
  log('ChannelTemplateModal', 'handleOpen', 'Abrindo modal de template', payload)
  row.value = payload?.row ?? null
  isVisible.value = true
}

onMounted(() => {
  eventBus.on('modal.channelTemplate.open', handleOpen)
})

onBeforeUnmount(() => {
  eventBus.off('modal.channelTemplate.open', handleOpen)
})
</script>
